import React from 'react';
import { getTeamName } from '../../utils/teamUtils';

/**
 * TeamSelector Component
 * 
 * Dropdown component for selecting a team when user has multiple teams in a league.
 * Auto-selects when user has only one team.
 * 
 * @param {Array} teams - Array of team objects with id and name
 * @param {Array} userTeamIds - Array of team IDs that the user has claimed
 * @param {string} selectedTeamId - Currently selected team ID
 * @param {Function} onTeamChange - Callback when team selection changes
 * @param {string} leagueName - League name for display (optional)
 */
export default function TeamSelector({ 
  teams, 
  userTeamIds, 
  selectedTeamId, 
  onTeamChange,
  leagueName 
}) {
  // Filter teams to only show user's claimed teams
  const userTeams = teams.filter(team => userTeamIds.includes(team.id));

  // Auto-select if only one team
  React.useEffect(() => {
    if (userTeams.length === 1 && !selectedTeamId) {
      onTeamChange(userTeams[0].id);
    }
  }, [userTeams.length, selectedTeamId, onTeamChange]);

  // Don't render if no teams or only one team (auto-selected)
  if (userTeams.length === 0) {
    return null;
  }

  if (userTeams.length === 1) {
    // Single team - show as read-only display
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Team
        </label>
        <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-700">
          {getTeamName(userTeams[0].id, teams)}
        </div>
      </div>
    );
  }

  // Multiple teams - show dropdown
  return (
    <div>
      <label htmlFor="team-selector" className="block text-sm font-medium text-gray-700 mb-2">
        {leagueName ? `Select Team (${leagueName})` : 'Select Team'}
      </label>
      <select
        id="team-selector"
        value={selectedTeamId || ''}
        onChange={(e) => onTeamChange(e.target.value)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
        required
      >
        <option value="">Select a team</option>
        {userTeams.map(team => (
          <option key={team.id} value={team.id}>
            {team.name}
          </option>
        ))}
      </select>
    </div>
  );
}

